import React from 'react'
import { Box, Container, Typography, Button, Paper, Stack } from '@mui/material'

export interface DonateCtaSectionProps {
  /**
   * CTA headline
   */
  title: string

  /**
   * Supporting description text
   */
  description: string

  /**
   * Button text
   */
  buttonText: string

  /**
   * 501(c)(3) disclaimer configuration
   */
  disclaimer: {
    text: string
    visible: boolean
  }

  /**
   * Callback when donate button is clicked
   */
  onDonateClick: () => void
}

/**
 * Final CTA section with 501(c)(3) disclaimer
 * Encourages users to complete their donation
 */
export const DonateCtaSection: React.FC<DonateCtaSectionProps> = ({
  title,
  description,
  buttonText,
  disclaimer,
  onDonateClick,
}) => {
  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        bgcolor: 'background.paper',
      }}
      aria-labelledby="final-cta-title"
    >
      <Container maxWidth="md">
        <Stack spacing={{ xs: 3, sm: 4 }} alignItems="center">
          {/* Headline */}
          <Typography
            id="final-cta-title"
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 700,
              color: 'text.primary',
              textAlign: 'center',
            }}
          >
            {title}
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' },
              color: 'text.secondary',
              textAlign: 'center',
              maxWidth: '600px',
            }}
          >
            {description}
          </Typography>

          {/* 501(c)(3) Disclaimer */}
          {disclaimer.visible && (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                width: '100%',
              }}
              role="note"
              aria-label="Tax-exempt status disclaimer"
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  color: 'text.secondary',
                  lineHeight: 1.6,
                }}
              >
                <strong>Tax-Exempt Status:</strong> {disclaimer.text}
              </Typography>
            </Paper>
          )}

          {/* Primary CTA Button */}
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={onDonateClick}
            aria-label="Make your donation contribution today"
            sx={{
              py: 2,
              px: 6,
              fontSize: { xs: '1rem', sm: '1.125rem' },
              fontWeight: 600,
              minHeight: 56, // Accessibility touch target
              minWidth: 200,
              textTransform: 'none',
            }}
          >
            {buttonText}
          </Button>
        </Stack>
      </Container>
    </Box>
  )
}
